import React from 'react';
import { AccessibleDivButton } from '@/components';
import { BodyMediumEmphasized, BodySmall, BodySmallMidEmphasis } from '@/design-system';
import { FollowButton } from '@/pages/OrgProfile/components/FollowButton/FollowButton';
import colors from '@/theme/colors';
import { APP_ROUTES } from '@/config/constants';
import { useNavigateWithTracking } from '@/hooks';
import { TrackingEvents, TrackingStage } from '@/config/tracking';
import { generatePath } from 'react-router-dom';
import { cx } from '@emotion/css';
import styles from './OrganizationCard.styles';

export interface Organization {
  id: string;
  orgId: number;
  name: string;
  initials: string;
  avatarColor: string;
  address: string;
  flyerCount: number;
  hasNewFlyers: boolean;
  approvedFlyerCount: number;
  activeFlyersCount: number;
  isFollowing: boolean;
}

interface OrganizationCardProps {
  organization: Organization;
  isInactive: boolean;
}

const getFlyerLabel = (org: Organization, isInactive: boolean): string => {
  if (isInactive) {
    return 'Inactive';
  }
  if (org.hasNewFlyers) {
    return `${org.flyerCount} new ${org.flyerCount === 1 ? 'flyer' : 'flyers'}`;
  }
  if (!org.hasNewFlyers && org.flyerCount === 0) {
    return 'Expired flyers only';
  }
  return `${org.flyerCount} active ${org.flyerCount === 1 ? 'flyer' : 'flyers'}`;
};

export const OrganizationCard: React.FC<OrganizationCardProps> = ({
  organization: org,
  isInactive,
}) => {
  const navigateWithTracking = useNavigateWithTracking();

  const onClick = () => {
    if (isInactive) return;

    navigateWithTracking(generatePath(APP_ROUTES.orgProfile, { id: String(org.orgId) }), {
      event_detail_from: 'following_organizations',
      event_from: TrackingEvents.browseContent,
      stage_from: TrackingStage.discovery,
    });
  };

  const CardContent = isInactive ? 'div' : AccessibleDivButton;

  return (
    <li className={styles.item}>
      <div className={styles.card}>
        <CardContent
          className={cx(styles.cardButton, isInactive && styles.cardContentInactive)}
          onClick={isInactive ? undefined : onClick}
          aria-label={
            isInactive ? undefined : `View ${org.name}, ${getFlyerLabel(org, isInactive)}`
          }
        >
          <div className={styles.avatarWrapper}>
            {org.hasNewFlyers && <span className={styles.activeDot} aria-hidden="true" />}
            <div
              className={isInactive ? styles.avatarInactive : styles.avatar}
              style={isInactive ? undefined : { backgroundColor: org.avatarColor }}
              aria-hidden="true"
            >
              {org.initials}
            </div>
          </div>

          <div className={styles.info}>
            <BodyMediumEmphasized
              className={styles.orgName}
              color={isInactive ? colors.neutralAsh : colors.prussian}
            >
              {org.name}
            </BodyMediumEmphasized>
            {org.address && (
              <BodySmall
                className={styles.location}
                color={isInactive ? colors.neutralAsh : colors.slate}
              >
                {org.address}
              </BodySmall>
            )}
            {org.hasNewFlyers ? (
              <BodySmallMidEmphasis color={colors.jungle}>
                {getFlyerLabel(org, isInactive)}
              </BodySmallMidEmphasis>
            ) : (
              <BodySmall color={isInactive ? colors.neutralAsh : colors.slate}>
                {getFlyerLabel(org, isInactive)}
              </BodySmall>
            )}
          </div>
        </CardContent>

        <FollowButton
          orgId={org.orgId}
          orgName={org.name}
          showViewFollowingCta={false}
          isFollowingInitial={org.isFollowing}
        />
      </div>
    </li>
  );
};
